import { useEffect, useState } from "react";
import { mutate } from "swr";

import { apiFetch } from "../../api/client";
import { toast } from "../../lib/toast";
import "../CreateProjectModal.css";

interface Props {
  paperId: number;
  paperName: string;
  onClose: () => void;
  onDeleted: () => void;
}

export function DeletePaperModal({ paperId, paperName, onClose, onDeleted }: Props) {
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !submitting) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  async function onConfirm() {
    setSubmitting(true);
    try {
      const res = await apiFetch(`/api/papers/${paperId}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      void mutate((key: unknown) => typeof key === "string" && key.startsWith("/api/projects/"));
      toast("Paper deleted");
      onDeleted();
    } catch {
      toast("Delete failed — check server logs");
      setSubmitting(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="modal-card" role="dialog" aria-modal="true" aria-label="Delete paper">
        <button
          type="button"
          className="modal-close"
          onClick={onClose}
          disabled={submitting}
          aria-label="Close"
        >
          ✕
        </button>

        <h2 className="modal-card__title">Delete paper</h2>
        <p className="wipe-paper-name">{paperName}</p>

        <div className="wipe-warning">
          This will permanently delete the paper, all its annotations, and the uploaded PDF.
          This action cannot be undone.
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="modal-btn modal-btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="modal-btn modal-btn--danger"
            disabled={submitting}
            onClick={() => void onConfirm()}
          >
            {submitting ? "Deleting…" : "Delete paper"}
          </button>
        </div>
      </div>
    </div>
  );
}
